import { Link } from "react-router-dom";
import {
  ShieldCheck, Check, MonitorSmartphone, DatabaseBackup, KeyRound,
  MailWarning, Wifi, Umbrella, Percent, PhoneCall, ArrowRight, Sparkles, BadgeCheck,
} from "lucide-react";
import { Reveal, Stagger, StaggerItem } from "@/components/motion/Reveal";
import { CTAButton } from "@/components/CTAButton";
import { Seo } from "@/components/Seo";
import { useState } from "react";
import { PromoBookingDialog } from "@/components/PromoBookingDialog";
import { COMPANY } from "@/lib/content";

const Eyebrow = ({ children, dark }) => (
  <span className={`text-xs uppercase tracking-[0.25em] font-medium ${dark ? "text-amber-500" : "text-amber-600"}`}>{children}</span>
);

/* Ce que passe en revue l'audit — ancré sur le périmètre cybersécurité d'Umbo */
const CHECKS = [
  { icon: MonitorSmartphone, title: "La protection de vos postes", text: "Antivirus, protection contre les rançongiciels et les logiciels non autorisés : ce qui est en place, ce qui manque." },
  { icon: DatabaseBackup, title: "Vos sauvegardes", text: "Existent-elles vraiment, et surtout : fonctionneraient-elles le jour où vous en auriez besoin ?" },
  { icon: KeyRound, title: "Vos mots de passe", text: "Robustesse, réutilisation, partage : l'un des premiers points d'entrée des attaques." },
  { icon: MailWarning, title: "Votre exposition aux arnaques", text: "Hameçonnage, fausses factures, usurpation : votre niveau de risque et les réflexes à acquérir." },
  { icon: Wifi, title: "Votre réseau et votre wifi", text: "Pare-feu, cloisonnement, wifi visiteurs : les portes ouvertes que l'on oublie souvent." },
  { icon: Umbrella, title: "Votre assurabilité", text: "Où vous vous situez face aux exigences que les assureurs cyber demandent aujourd'hui." },
];

const PROOF = [
  "80 % de réduction sur le tarif habituel",
  "100 € déduits si vous souscrivez ensuite",
  "Sans engagement, dans les Hauts-de-France",
];

const STEPS = [
  { n: "01", title: "Vous réservez", text: "En 2 minutes via le formulaire, ou par téléphone. On convient d'un créneau." },
  { n: "02", title: "Nous auditons", text: "Nous passons en revue votre sécurité, sur place ou à distance selon votre situation." },
  { n: "03", title: "Vous décidez", text: "Vous recevez un état des lieux clair et des priorités. Libre à vous de donner suite." },
];

const FAQS = [
  { q: "L'audit est-il vraiment déduit si je souscris ?", a: "Oui. Si vous souscrivez une offre Umbo à la suite de l'audit, les 100 € HT sont intégralement déduits de votre première facture. Autrement dit, l'audit peut ne rien vous coûter." },
  { q: "Suis-je obligé de souscrire ensuite ?", a: "Non. L'audit est sans engagement. Vous repartez avec vos conclusions et vos priorités, et vous restez entièrement libre de donner suite ou non." },
  { q: "Pour quelle taille d'entreprise ?", a: "Les TPE et petites entreprises des Hauts-de-France, jusqu'à une vingtaine de postes. Au-delà, contactez-nous : nous adaptons." },
];

/* ---------------- HERO ---------------- */
const Hero = ({ onReserve }) => {
  return (
    <section className="relative bg-navy grain overflow-hidden pt-36 pb-20 md:pt-44 md:pb-28" data-testid="promo-hero">
      <div className="pointer-events-none absolute -bottom-40 -left-24 w-[46rem] h-[46rem] rounded-full bg-amber-500/20 blur-[130px]" aria-hidden />
      <div className="pointer-events-none absolute -top-10 right-0 w-[34rem] h-[34rem] rounded-full bg-[#0b264e]/50 blur-[120px]" aria-hidden />

      <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-12 gap-14 items-center">
        <div className="lg:col-span-7">
          <Reveal>
            <div className="inline-flex items-center gap-2 rounded-full bg-amber-500/10 border border-amber-500/30 px-4 py-1.5">
              <Sparkles className="w-4 h-4 text-amber-400" />
              <span className="text-amber-400 text-sm font-medium">Offre spéciale · Audit cybersécurité</span>
            </div>
          </Reveal>
          <Reveal delay={0.05}>
            <h1 className="mt-6 font-display text-4xl sm:text-5xl md:text-6xl font-bold tracking-tighter text-white text-balance leading-[1.02]">
              Votre audit cybersécurité à <span className="text-amber-500">100 € HT</span>, au lieu de 500 €.
            </h1>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-7 flex items-center gap-4 flex-wrap">
              <span className="font-display text-5xl md:text-6xl font-bold text-white tabular-nums">100 €<span className="text-2xl text-white/60 font-medium"> HT</span></span>
              <span className="text-slate-400 text-2xl line-through">500 € HT</span>
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-600 text-white text-sm font-semibold px-3 py-1"><Percent className="w-3.5 h-3.5" /> −80 %</span>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-7 text-lg md:text-xl text-slate-300 max-w-2xl leading-relaxed">
              Un état des lieux complet de la sécurité de votre entreprise. Et si vous souscrivez ensuite
              une offre Umbo, les <strong className="text-white">100 € sont intégralement déduits</strong> — l'audit peut donc ne rien vous coûter.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <CTAButton size="lg" withArrow onClick={onReserve} data-testid="promo-hero-cta">Réserver mon audit</CTAButton>
              <a href={`tel:${COMPANY.phoneHref}`} className="inline-flex items-center justify-center gap-2 rounded-full border border-white/25 text-white px-8 py-4 text-base font-medium hover:bg-white/10 transition-colors">
                <PhoneCall className="w-4 h-4 text-amber-500" /> {COMPANY.phone}
              </a>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-5">
          <Reveal delay={0.15}>
            <div className="rounded-2xl border border-white/12 bg-white/[0.05] backdrop-blur-md p-8 shadow-[0_24px_60px_-24px_rgba(0,0,0,0.6)]">
              <p className="text-amber-400 text-[11px] uppercase tracking-[0.22em] font-semibold">Ce que comprend l'offre</p>
              <ul className="mt-6 space-y-4">
                {PROOF.map((p) => (
                  <li key={p} className="flex items-start gap-3">
                    <span className="mt-0.5 shrink-0 w-5 h-5 rounded-full bg-amber-500/15 grid place-items-center"><Check className="w-3 h-3 text-amber-400" /></span>
                    <span className="text-white/90 text-sm leading-snug">{p}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-7 pt-5 border-t border-white/10">
                <p className="text-white/60 text-sm">L'audit vous revient à</p>
                <p className="font-display text-3xl font-bold text-white mt-1">entre 0 € et 100 € HT</p>
                <p className="text-white/50 text-xs mt-1">selon que vous souscrivez ou non une offre ensuite.</p>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

/* ---------------- CE QUE COMPREND L'AUDIT ---------------- */
const WhatIncluded = () => (
  <section className="bg-white py-24 md:py-32" data-testid="promo-checks">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <div className="max-w-2xl">
        <Reveal><Eyebrow>Ce que nous vérifions</Eyebrow></Reveal>
        <Reveal delay={0.05}><h2 className="mt-5 font-display text-4xl sm:text-5xl font-semibold tracking-tight text-navy text-balance">Un vrai état des lieux, pas une simple case cochée.</h2></Reveal>
        <Reveal delay={0.1}><p className="mt-6 text-lg text-slate-600 leading-relaxed">Nous passons en revue les points qui font vraiment la différence pour une petite entreprise. À la fin, vous savez précisément où vous en êtes.</p></Reveal>
      </div>
      <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200 rounded-xl overflow-hidden shadow-[0_30px_60px_-40px_rgba(7,27,58,0.35)]">
        {CHECKS.map((c, i) => (
          <Reveal key={c.title} delay={i * 0.04} className="group bg-white p-8 hover:bg-[#f8fafc] transition-colors">
            <div className="w-12 h-12 rounded-lg bg-navy grid place-items-center group-hover:bg-navy-light transition-colors"><c.icon className="w-6 h-6 text-amber-500" /></div>
            <h3 className="mt-5 font-display text-xl font-medium text-navy">{c.title}</h3>
            <p className="mt-3 text-slate-600 leading-relaxed">{c.text}</p>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.1}>
        <p className="mt-8 text-slate-600 flex items-start gap-2 max-w-3xl">
          <BadgeCheck className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
          <span>À l'issue de l'audit, vous recevez un <strong className="text-navy">état des lieux clair et des priorités concrètes</strong> — que vous deveniez client ou non.</span>
        </p>
      </Reveal>
    </div>
  </section>
);

/* ---------------- LE PRIX EN CLAIR ---------------- */
const PriceRow = ({ label, value, strike, amber, big }) => (
  <div className={`flex items-baseline justify-between gap-4 ${big ? "pt-5 mt-1 border-t border-white/12" : "py-3"}`}>
    <span className={`${big ? "text-white font-medium text-lg" : "text-slate-300"} leading-snug`}>{label}</span>
    <span className={`font-display tabular-nums shrink-0 ${big ? "text-4xl font-bold text-white" : amber ? "text-xl font-semibold text-amber-400" : strike ? "text-xl text-slate-500 line-through" : "text-2xl font-bold text-white"}`}>{value}</span>
  </div>
);

const PriceBreakdown = ({ onReserve }) => {
  return (
    <section className="relative bg-navy-dark grain overflow-hidden py-24 md:py-32" data-testid="promo-price">
      <div className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[54rem] h-[30rem] rounded-full bg-amber-500/12 blur-[140px]" aria-hidden />
      <div className="relative z-10 mx-auto max-w-4xl px-5 sm:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <Reveal><Eyebrow dark>Le prix, en clair</Eyebrow></Reveal>
          <Reveal delay={0.05}><h2 className="mt-5 font-display text-4xl sm:text-5xl font-semibold tracking-tight text-white text-balance">Au pire 100 €. Au mieux, rien du tout.</h2></Reveal>
        </div>
        <Reveal delay={0.1}>
          <div className="mt-12 max-w-xl mx-auto rounded-2xl border border-white/12 bg-white/[0.04] backdrop-blur-sm p-8 sm:p-10">
            <PriceRow label="Tarif habituel de l'audit" value="500 € HT" strike />
            <PriceRow label="Votre tarif aujourd'hui" value="100 € HT" />
            <PriceRow label="Déduit si vous souscrivez une offre Umbo" value="− 100 €" amber />
            <PriceRow label="L'audit peut donc vous revenir à" value="0 €" big />
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="mt-10 text-center">
            <CTAButton size="lg" withArrow onClick={onReserve} data-testid="promo-price-cta">Réserver mon audit à 100 € HT</CTAButton>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

/* ---------------- COMMENT EN PROFITER ---------------- */
const HowTo = () => (
  <section className="bg-[#f8fafc] py-24 md:py-32" data-testid="promo-how">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <div className="max-w-2xl">
        <Reveal><Eyebrow>Comment en profiter</Eyebrow></Reveal>
        <Reveal delay={0.05}><h2 className="mt-5 font-display text-4xl sm:text-5xl font-semibold tracking-tight text-navy text-balance">Trois étapes, sans prise de tête.</h2></Reveal>
      </div>
      <div className="mt-20 relative">
        <div className="hidden md:block absolute top-8 left-0 right-0 h-px bg-gradient-to-r from-transparent via-slate-300 to-transparent" aria-hidden />
        <div className="grid md:grid-cols-3 gap-y-12 md:gap-x-10">
          {STEPS.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.12}>
              <div className="relative flex flex-col">
                <div className="relative z-10 w-16 h-16 rounded-2xl bg-navy grid place-items-center shadow-[0_16px_40px_-16px_rgba(7,27,58,0.6)] ring-8 ring-[#f8fafc]">
                  <span className="font-display text-xl font-bold text-amber-500 tabular-nums">{s.n}</span>
                </div>
                <h3 className="mt-7 font-display text-xl font-medium text-navy">{s.title}</h3>
                <p className="mt-3 text-slate-600 leading-relaxed max-w-sm">{s.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
);

/* ---------------- RÉASSURANCE ---------------- */
const Reassure = () => (
  <section className="bg-white py-20 md:py-24 border-y border-slate-200" data-testid="promo-reassure">
    <div className="mx-auto max-w-5xl px-5 sm:px-8 grid md:grid-cols-3 gap-8">
      {[
        { icon: ShieldCheck, title: "La même protection pour tous", text: "Pas de formule au rabais : le niveau de sécurité le plus élevé, quelle que soit votre taille." },
        { icon: Check, title: "Un seul interlocuteur", text: "Une entreprise locale des Hauts-de-France, jamais un centre d'appel à l'autre bout du monde." },
        { icon: Umbrella, title: "Sans engagement", text: "Vous repartez avec vos conclusions. Donner suite reste entièrement votre choix." },
      ].map((b) => (
        <Reveal key={b.title}>
          <div className="w-12 h-12 rounded-lg bg-navy grid place-items-center"><b.icon className="w-6 h-6 text-amber-500" /></div>
          <h3 className="mt-5 font-display text-lg font-medium text-navy">{b.title}</h3>
          <p className="mt-2 text-slate-600 leading-relaxed">{b.text}</p>
        </Reveal>
      ))}
    </div>
  </section>
);

/* ---------------- FAQ ---------------- */
const FaqBlock = () => (
  <section className="bg-[#f8fafc] py-24 md:py-28" data-testid="promo-faq">
    <div className="mx-auto max-w-4xl px-5 sm:px-8">
      <div className="max-w-2xl mb-12">
        <Reveal><Eyebrow>Bon à savoir</Eyebrow></Reveal>
        <Reveal delay={0.05}><h2 className="mt-5 font-display text-4xl sm:text-5xl font-semibold tracking-tight text-navy">Les questions que vous vous posez.</h2></Reveal>
      </div>
      <div className="space-y-px bg-slate-200 border border-slate-200 rounded-xl overflow-hidden">
        {FAQS.map((f, i) => (
          <Reveal key={i} className="bg-white p-7 sm:p-8">
            <h3 className="font-display text-lg font-medium text-navy">{f.q}</h3>
            <p className="mt-3 text-slate-600 leading-relaxed">{f.a}</p>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ---------------- CTA FINAL ---------------- */
const FinalCTA = ({ onReserve }) => {
  return (
    <section className="relative bg-navy grain overflow-hidden py-28 md:py-36" data-testid="promo-final">
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-navy-dark/50 via-navy to-navy-dark/50" aria-hidden />
      <div className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[54rem] h-[30rem] rounded-full bg-amber-500/15 blur-[140px]" aria-hidden />
      <div className="relative z-10 mx-auto max-w-3xl px-5 sm:px-8 text-center">
        <Reveal>
          <div className="inline-flex items-center gap-2 rounded-full bg-amber-500/10 border border-amber-500/30 px-4 py-1.5 mb-6">
            <Sparkles className="w-4 h-4 text-amber-400" /><span className="text-amber-400 text-sm font-medium">100 € HT · au lieu de 500 €</span>
          </div>
        </Reveal>
        <Reveal delay={0.05}><h2 className="font-display text-4xl sm:text-5xl md:text-6xl font-semibold tracking-tighter text-white text-balance">Faites le point sur votre sécurité.</h2></Reveal>
        <Reveal delay={0.1}><p className="mt-6 text-lg md:text-xl text-slate-300 max-w-2xl mx-auto">Réservez votre audit cybersécurité en 2 minutes. Sans engagement — et déduit si vous souscrivez ensuite.</p></Reveal>
        <Reveal delay={0.16}>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <CTAButton size="lg" withArrow onClick={onReserve} data-testid="promo-final-cta">Réserver mon audit</CTAButton>
            <a href={`tel:${COMPANY.phoneHref}`} className="inline-flex items-center justify-center gap-2 rounded-full border border-white/25 text-white px-8 py-4 text-base font-medium hover:bg-white/10 transition-colors">
              <PhoneCall className="w-4 h-4 text-amber-500" /> {COMPANY.phone}
            </a>
          </div>
        </Reveal>
        <Reveal delay={0.22}>
          <p className="mt-8 text-slate-400 text-sm">
            Une question d'abord ? <Link to="/cybersecurite" className="text-amber-400 underline underline-offset-2 hover:text-amber-300">Voir notre approche de la cybersécurité</Link>
          </p>
        </Reveal>
      </div>
    </section>
  );
};

export default function Promo() {
  const [bookingOpen, setBookingOpen] = useState(false);
  const openBooking = () => setBookingOpen(true);
  return (
    <>
      <Seo
        title="Audit cybersécurité TPE à 100 € HT (au lieu de 500 €)"
        description="Offre spéciale : votre audit cybersécurité TPE à 100 € HT au lieu de 500 €. Les 100 € sont déduits si vous souscrivez une offre Umbo — l'audit peut ne rien vous coûter. Sans engagement, dans les Hauts-de-France."
        path="/promo"
      />
      <Hero onReserve={openBooking} />
      <WhatIncluded />
      <PriceBreakdown onReserve={openBooking} />
      <HowTo />
      <Reassure />
      <FaqBlock />
      <FinalCTA onReserve={openBooking} />
      <PromoBookingDialog open={bookingOpen} onOpenChange={setBookingOpen} />
    </>
  );
}
